import { gql } from "@apollo/client";

// Query to get workspace details with full statistics
export const WORKSPACE_DETAILS_QUERY = gql`
  query WorkspaceDetails($workspaceId: String!) {
    workspaceDetails(workspaceId: $workspaceId) {
      id
      description
      owner {
        id
        name
        surname
        email
      }
      stats {
        totalUsers
        totalPlayers
        totalCoaches
        totalTeams
        playersWithoutUsers
        usersWithoutPlayers
      }
      createdAt
    }
  }
`;

// Query to get all users in workspace
export const WORKSPACE_USERS_QUERY = gql`
  query WorkspaceUsers($workspaceId: String!) {
    workspaceUsers(workspaceId: $workspaceId) {
      id
      name
      surname
      email
      role
      avatarUrl
      workspace
      createdAt
      # N:N relationship - list of players this user has access to
      linkedPlayers {
        id
        name
        surname
        number
        position
        avatarUrl
        team {
          id
          name
        }
      }
    }
  }
`;

// Query to get all players with their linked users
export const PLAYERS_WITH_USERS_QUERY = gql`
  query PlayersWithUsers($workspaceId: String!) {
    playersWithUsers(workspaceId: $workspaceId) {
      id
      name
      surname
      email
      avatarUrl
      position
      number
      team {
        id
        name
      }
      linkedUsers {
        id
        name
        surname
        email
        role
        avatarUrl
      }
      totalGoals
      totalMatches
    }
  }
`;

// Query to get all coaches in workspace
export const WORKSPACE_COACHES_QUERY = gql`
  query WorkspaceCoaches($workspaceId: String!) {
    workspaceCoaches(workspaceId: $workspaceId) {
      id
      name
      surname
      workspace
    }
  }
`;

// Query to get available users (not linked to a specific player) - N:N relationship
export const AVAILABLE_USERS_QUERY = gql`
  query AvailableUsers($workspaceId: String!, $playerId: ID!) {
    availableUsers(workspaceId: $workspaceId, playerId: $playerId) {
      id
      name
      surname
      email
      role
      avatarUrl
    }
  }
`;

// Mutation to link a user to a player
export const LINK_USER_TO_PLAYER = gql`
  mutation LinkUserToPlayer($playerId: ID!, $userId: ID!) {
    linkUserToPlayer(playerId: $playerId, userId: $userId) {
      success
      message
      player {
        id
        linkedUsers {
          id
          name
          surname
          email
        }
      }
    }
  }
`;

// Mutation to unlink a user from a player
export const UNLINK_USER_FROM_PLAYER = gql`
  mutation UnlinkUserFromPlayer($playerId: ID!, $userId: ID!) {
    unlinkUserFromPlayer(playerId: $playerId, userId: $userId) {
      success
      message
      player {
        id
        linkedUsers {
          id
          name
          surname
          email
        }
      }
    }
  }
`;

// Mutation to create a new coach
export const CREATE_COACH = gql`
  mutation CreateCoach(
    $name: String!
    $surname: String!
    $email: String!
    $workspaceId: String!
  ) {
    createCoach(
      input: {
        name: $name
        surname: $surname
        email: $email
        workspaceId: $workspaceId
      }
    ) {
      id
      name
      surname
    }
  }
`;

// Mutation to update a coach
export const UPDATE_COACH = gql`
  mutation UpdateCoach(
    $id: ID!
    $name: String
    $surname: String
    $email: String
    $specialty: String
    $telephone: String
  ) {
    updateCoach(
      id: $id
      input: {
        name: $name
        surname: $surname
        email: $email
        specialty: $specialty
        telephone: $telephone
      }
    ) {
      id
      name
      surname
    }
  }
`;

// Mutation to delete a coach
export const DELETE_COACH = gql`
  mutation DeleteCoach($id: ID!) {
    deleteCoach(id: $id) {
      success
      message
    }
  }
`;

// Mutation to change user role
export const CHANGE_USER_ROLE = gql`
  mutation ChangeUserRole($userId: ID!, $newRole: String!) {
    changeUserRole(userId: $userId, newRole: $newRole) {
      success
      message
      user {
        id
        name
        surname
        email
        role
      }
    }
  }
`;

// Mutation to invite a new user to a player
export const INVITE_PLAYER = gql`
  mutation InvitePlayer($id: ID!, $email: String!, $role: String!) {
    invitePlayer(id: $id, email: $email, role: $role)
  }
`;

// Mutation to invite a coach with a simple process (QR code)
export const INVITE_COACH = gql`
  mutation InviteCoach($workspaceId: String!) {
    inviteCoachToWorkspace(workspaceId: $workspaceId) {
      success
      message
      invitationToken
      invitationUrl
    }
  }
`;

// Combined query to get all workspace management data in one request
export const WORKSPACE_MANAGEMENT_DATA = gql`
  query WorkspaceManagementData($workspaceId: String!) {
    workspaceDetails(workspaceId: $workspaceId) {
      id
      description
      owner {
        id
        name
        surname
        email
      }
      stats {
        totalUsers
        totalPlayers
        totalCoaches
        totalTeams
        playersWithoutUsers
        usersWithoutPlayers
      }
      createdAt
    }
    workspaceUsers(workspaceId: $workspaceId) {
      id
      name
      surname
      email
      role
      avatarUrl
      workspace
      playerLink
      createdAt
      linkedPlayers {
        id
        name
        surname
        number
        position
        avatarUrl
        team {
          id
          name
        }
      }
    }
    playersWithUsers(workspaceId: $workspaceId) {
      id
      name
      surname
      email
      avatarUrl
      position
      number
      team {
        id
        name
      }
      linkedUsers {
        id
        name
        surname
        email
        role
        avatarUrl
      }
      totalGoals
      totalMatches
    }
    workspaceCoaches(workspaceId: $workspaceId) {
      id
      name
      surname
      workspace
    }
  }
`;
